export interface PrestataireDevisLike {
  id?: number | string;
  created_at?: string;
  updated_at?: string;
  statut?: string;
  status?: string;
}

export interface PrestataireDemandLike {
  id?: number | string;
  statut?: string;
  status?: string;
  attent?: string;
  job?: string;
  devis?: PrestataireDevisLike[];
  meetings?: unknown[];
  imagePreuveBefore?: unknown[];
}

/** Pick the most recent devis entry (not always index 0 after refuse/update cycles). */
export function resolveLatestDevisId(
  devis: PrestataireDevisLike[] | null | undefined
): string | number | null {
  if (!devis?.length) {
    return null;
  }
  if (devis.length === 1) {
    return devis[0]?.id ?? null;
  }

  const safeTime = (value: unknown): number => {
    if (value == null || value === "") {
      return Number.NEGATIVE_INFINITY;
    }
    const time = new Date(String(value)).getTime();
    return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
  };

  const sorted = [...devis]
    .filter((entry) => entry?.id != null)
    .sort((a, b) => {
      const bTime = Math.max(safeTime(b.updated_at), safeTime(b.created_at));
      const aTime = Math.max(safeTime(a.updated_at), safeTime(a.created_at));
      return bTime - aTime;
    });

  return sorted[0]?.id ?? devis[devis.length - 1]?.id ?? null;
}

export type PrestataireDemandRouteResult =
  | { action: "navigate"; path: string }
  | { action: "stay"; showDevisModal?: boolean };

/**
 * Shared routing logic for prestataire demand lifecycle.
 * Mirrors demande-list.validateRoutes() and provider.service.prestataireDemands().
 */
export function resolvePrestataireDemandRoute(
  demand: PrestataireDemandLike | null | undefined
): string | null {
  const result = resolvePrestataireDemandRouteResult(demand);
  return result.action === "navigate" ? result.path : null;
}

/** Direct prestataire devis screen path from devis id (push/deep link). */
export function resolvePrestataireDevisResumePath(
  devisId: string | number | null | undefined
): string | null {
  if (devisId == null || devisId === "") {
    return null;
  }
  return `/prestataire/devis-resume/${devisId}`;
}

export function resolvePrestataireDemandRouteResult(
  demand: PrestataireDemandLike | null | undefined
): PrestataireDemandRouteResult {
  if (!demand) {
    return { action: "stay" };
  }

  const statut = demand.statut ?? demand.status;
  const attent = demand.attent;
  const meetings = demand.meetings;
  const devis = demand.devis;
  const job = demand.job;
  const isTransporterDemande = job === "Transporteur";

  if (
    statut === "INPROGRESS" &&
    attent === "CONFIRMED" &&
    !meetings?.length &&
    !devis?.length
  ) {
    if (isTransporterDemande) {
      return { action: "navigate", path: "/prestataire/demande-images/transporteur" };
    }
    return { action: "stay", showDevisModal: true };
  }

  if (statut === "INPROGRESS" && attent === "CONFIRMED" && devis?.length) {
    const devisId = resolveLatestDevisId(devis);
    if (!devisId) {
      return { action: "stay" };
    }
    if ((demand.imagePreuveBefore?.length ?? 0) > 0) {
      return { action: "navigate", path: `/prestataire/demande-images/${devisId}` };
    }
    return { action: "navigate", path: `/prestataire/devis-resume/${devisId}` };
  }

  if (statut === "ONROAD" && attent === "CONFIRMED" && devis?.length) {
    const devisId = resolveLatestDevisId(devis);
    if (!devisId) {
      return { action: "stay" };
    }
    return { action: "navigate", path: `/prestataire/devis-resume/${devisId}` };
  }

  if (statut === "INPROGRESS" && attent === "CONFIRMED" && meetings?.length) {
    const demandeId = demand.id;
    if (!demandeId) {
      return { action: "stay" };
    }
    return { action: "navigate", path: `/prestataire/add-rendez-vous/${demandeId}` };
  }

  if (
    (statut === "INPROGRESS" && attent === "CONFIRMED" && !meetings?.length && !devis?.length) ||
    statut === "ONROAD"
  ) {
    return { action: "navigate", path: "/prestataire/demandes" };
  }

  if (demand && (statut === "INPROGRESS" || statut === "ONROAD")) {
    return { action: "navigate", path: "/prestataire/demandes" };
  }

  return { action: "stay" };
}

export function extractDemandeForRoute(
  stored: Record<string, any> | null | undefined
): PrestataireDemandLike | null {
  if (!stored) {
    return null;
  }
  const demande = (stored["demande"] ?? stored) as PrestataireDemandLike;
  return demande ?? null;
}

export function normalizeRoutePath(url: string): string {
  const withoutQuery = (url || "").split("?")[0].split("#")[0];
  const decoded = decodeURIComponent(withoutQuery);
  if (decoded.length > 1 && decoded.endsWith("/")) {
    return decoded.slice(0, -1);
  }
  return decoded || "/";
}

export function isSameRoutePath(currentUrl: string, targetPath: string): boolean {
  return normalizeRoutePath(currentUrl) === normalizeRoutePath(targetPath);
}
